// Learning Kit — treasure-hunt exercises that surface on /home during the
// trial and empty as the user completes them. Registry-driven so new cards
// slot in without UI work.
//
// Each card teaches ONE thing, grounded in a real feature. Completion is
// signal-based (we detect the user doing the thing) or localStorage-based
// (user taps "got it"). Tasks auto-dismiss on completion.
//
// Cards optionally carry a `personas: [...]` field — when the user matches
// any of those tags, the card bubbles toward the front of the queue. Cards
// without `personas` are universal and keep their registry-order slot.
// Bias, not gate — every user eventually sees every card.

import { sortByPersona } from "./persona";

export const LEARNING_CARDS = [
  {
    slug: "build-your-first-cron",
    title: "Build your first cron — through chat",
    hook: "Watch for news about anything you care about. A band, a market, a topic. Fülkit pulls a fresh digest on your schedule and drops it on your dashboard.",
    cta: {
      label: "Open chat",
      href: "/chat",
      hint: "Say: help me build a routine for [your thing]",
    },
    bonus: "Chat workshops it with you — scope, format, what to skip — then fires the first run immediately so you see it work.",
    // Lesson opener — preloaded as the first assistant message on /chat so
    // the user arrives mid-context, not from a blank greeting. Replaces the
    // Haiku generic greeting via sessionStorage handoff (set by the CTA,
    // consumed by the chat greeting effect).
    chatOpener: "Hey. We're building your first cron — a task that runs on a schedule. Like \"every Monday morning, pull news about my favorite band, drop it on my dashboard.\" What's something you want to keep track of daily or weekly? I'll help you shape the prompt so the first run lands clean.",
    completion: { kind: "state", check: "has_any_routine" },
    stage: 1,
    // Universal — cron is the headline Fülkit feature for everyone.
  },
  {
    slug: "teach-chappie-one-thing",
    title: "Teach Chappie something about you",
    hook: "A question a day — answer one and watch Fülkit start to feel like it actually knows you.",
    cta: {
      label: "Answer today's question",
      // inlineReveal: when clicked, expands the referenced panel directly
      // under the CTA instead of scrolling away. Keeps the teaching moment
      // in one place — no page jerk, no disconnect between button and
      // target. Consumed by WelcomeKitCard; "drip-question" maps to
      // WhatIdLikeToKnowCard in embedded mode.
      inlineReveal: "drip-question",
    },
    bonus: "Rather skip one? Tap Tomorrow in the question — a fresh one surfaces next.",
    completion: { kind: "state", check: "has_any_drip_answer" },
    stage: 1,
  },
  {
    slug: "see-memory-index",
    title: "See what Fülkit remembers about you",
    hook: "Your memory index is everything Fülkit has picked up — onboarding answers, drip responses, integration harvests, every 'remember that' you've said. Editable and deletable.",
    cta: {
      label: "Open memory index",
      href: "/settings/what-i-know",
    },
    bonus: "Delete one thing. Then ask Chappie about it — watch the deletion land in real time.",
    completion: { kind: "state", check: "has_visited_memory_index" },
    stage: 1,
  },
  {
    slug: "tune-whispers",
    title: "Let Fülkit notice things for you",
    hook: "Whispers are unprompted observations — patterns, nudges, things worth saying. Tune the volume from off to chatty.",
    cta: {
      label: "Set whisper frequency",
      href: "/settings/ai",
    },
    bonus: "Off is fully silent — integrations keep harvesting, routines keep firing, whispers just don't surface.",
    completion: { kind: "state", check: "has_visited_ai_settings" },
    stage: 1,
    personas: ["family", "health"],
  },
  {
    slug: "try-tiny-fulkit",
    title: "Pop Fülkit from anywhere — CMD+Shift+C",
    hook: "Tiny Fülkit is a floating mini-chat window that opens over anything you're doing. Same brain, always within reach.",
    cta: {
      label: "Try it now",
      href: null,
      hint: "Press CMD+Shift+C (Ctrl+Shift+C on Windows)",
    },
    bonus: "Works inside Fülkit too — great for a side question without losing your main chat thread.",
    completion: { kind: "state", check: "has_opened_tiny_fulkit" },
    stage: 1,
    personas: ["developer", "creative"],
  },
  {
    slug: "stack-ful-pays",
    title: "Get paid when friends join",
    hook: "Every active referral = 100 Fül credits/mo toward your subscription. Stack enough and the app pays for itself.",
    cta: {
      label: "Get your referral link",
      href: "/settings?tab=referrals",
    },
    bonus: "Stripe Express Payouts kick in after your first stack lands — real money, not just credit.",
    completion: { kind: "state", check: "has_visited_referrals" },
    stage: 1,
  },
  {
    slug: "catch-morning-digest",
    title: "Catch tomorrow's Morning Digest",
    hook: "Fülkit quietly builds you a daily briefing — today's calendar, open actions, overdue threads, anything worth flagging. It shows up on /home around 6–8am.",
    cta: {
      label: "See the dashboard",
      href: "/home",
    },
    bonus: "Nothing worth saying today? The digest stays quiet. It won't pad the card.",
    completion: { kind: "manual" },
    stage: 1,
    personas: ["business", "family", "health"],
  },
  {
    slug: "peek-ful-gauge",
    title: "Peek at your Fül Gauge",
    hook: "That thin bar next to your greeting is your live message budget. No surprise invoices — you always know where you stand.",
    cta: {
      label: "See the gauge",
      scrollToId: "greeting",
    },
    bonus: "BYOK users see ∞. Owners see ∞. Everyone sees the truth.",
    completion: { kind: "manual" },
    stage: 1,
  },
  {
    slug: "try-actions-lens",
    title: "Flip between Life / Home / Work",
    hook: "Your Actions board has three lenses. Separation reduces cognitive load — when you're in Work mode, you shouldn't see the dry cleaning task.",
    cta: {
      label: "Open Actions",
      href: "/actions",
    },
    bonus: "Ask Chappie 'what's on my Home plate?' — same filtering, voice-driven.",
    completion: { kind: "state", check: "has_visited_actions" },
    stage: 1,
    personas: ["family", "business"],
  },
  {
    slug: "dig-a-set-in-fabric",
    title: "Dig a set in Fabric",
    hook: "Fabric is your music island. Search across Spotify, YouTube, and Apple Music at once. Fill a crate, stage a set, let B-Side curate if you want a second opinion.",
    // Discovery card — the user has to find the entry point themselves.
    // No navigate-CTA; instead, the card renders a locator (icon + text).
    // The actual nav target (MiniPlayer's Disc icon at the bottom of the
    // sidebar) pulses while `lk-visited-fabric` is unset; pulse stops
    // forever once the user clicks through. Future discovery cards
    // (Hum, etc.) follow the same { kind: "discovery", locator: {...} }
    // shape — see WelcomeKitCard's discovery render branch.
    kind: "discovery",
    locator: {
      text: "There's a record icon hiding in your sidebar — bottom-left.",
      iconName: "Disc",
    },
    bonus: "Find the printer icon on a Set — a procedural poster drops as a PNG. Favorite hidden treasure.",
    completion: { kind: "state", check: "has_visited_fabric" },
    stage: 1,
    personas: ["creative", "musician"],
  },
  {
    slug: "organize-with-threads",
    title: "Put work on a board — Threads",
    hook: "Threads is Fülkit's kanban: cards with status, priority, checklists, and folder routing. Built for projects with moving parts, not one-off tasks.",
    cta: {
      label: "Open Threads",
      href: "/threads",
    },
    bonus: "Ask Chappie 'turn this into a thread' mid-conversation — the kanban card drops in pre-populated.",
    completion: { kind: "state", check: "has_visited_threads" },
    stage: 1,
    personas: ["developer", "business", "learner"],
  },
  {
    slug: "tap-the-orb-hum",
    title: "Tap the orb — talk to The Hum",
    hook: "The Hum is voice mode. Tap the orb, speak, watch it file what you said. No transcript on screen — the orb is a listener, not a typewriter.",
    cta: {
      label: "Open The Hum",
      href: "/hum",
    },
    bonus: "60-second cap per tap. Everything gets transcribed server-side and filed into the right vault folder automatically.",
    completion: { kind: "state", check: "has_visited_hum" },
    stage: 1,
    personas: ["creative", "musician", "family"],
  },
  {
    slug: "upload-a-doc",
    title: "Drop a doc in chat — watch Fülkit file it for you",
    hook: "Drag a utility bill, insurance card, or kid's vaccine record straight into chat. Fülkit reads it, files it where it belongs (Personal, Business, Projects), and remembers it. Then ask 'when's my next premium due?' — done.",
    cta: {
      label: "Drop something in chat",
      href: "/chat",
    },
    chatOpener: "Hey. Drop a doc here — utility bill, insurance card, lease, kid's vaccine card, anything you'd usually lose. I'll read it, file it where it belongs, and remember it for next time. What've you got handy?",
    bonus: "Stack a few — utility, insurance, lease, school doc. Then say 'who's my electric provider?' or 'when's my registration up?'. Fülkit answers from your stuff, not the internet.",
    completion: { kind: "state", check: "has_uploaded_doc" },
    stage: 1,
    personas: ["family", "business", "learner"],
  },
];

// Helper — writes the LS flag AND dispatches the event WelcomeKitCard
// listens for. Use this from any same-window completion site (CMD+Shift+C
// hotkey, file-drop button, etc.) so the card re-checks state immediately
// instead of waiting for next nav. Sites where completion fires on a
// DIFFERENT page (visited-X cards) don't strictly need this — WelcomeKitCard
// remounts on /home re-entry — but adopting it everywhere keeps the pattern
// uniform + future-proofs against the WelcomeKitCard ever being mounted
// in additional surfaces.
//
// Pass any of the LS values from LEARNING_KIT_LS_KEYS below.
export function markLearningKitVisited(lsKey) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(lsKey, String(Date.now())); } catch {}
  try { window.dispatchEvent(new Event("fulkit:learning-kit-state-changed")); } catch {}
}

// localStorage keys for state-based completion — written by the target page/
// surface on mount (or on shortcut fire for tiny-fulkit). Kept in one place
// so checkers + mount effects can't drift.
export const LEARNING_KIT_LS_KEYS = {
  has_visited_memory_index: "lk-visited-memory-index",
  has_visited_ai_settings: "lk-visited-ai-settings",
  has_visited_referrals: "lk-visited-referrals",
  has_visited_actions: "lk-visited-actions",
  has_opened_tiny_fulkit: "lk-opened-tiny-fulkit",
  has_visited_fabric: "lk-visited-fabric",
  has_visited_threads: "lk-visited-threads",
  has_visited_hum: "lk-visited-hum",
  has_uploaded_doc: "lk-uploaded-doc",
};

// Return the next card the user hasn't completed yet. Dismissed-for-today
// and completion state live client-side (localStorage) for the MVP.
// Replace with /api/welcome-kit when multi-card scheduling + server-side
// persistence lands.
//
// `personas` (optional Set<string>) biases the order — cards whose `personas`
// array intersects the user's tags bubble first; remaining cards keep their
// registry order. No personas → current universal order.
export function nextCard({ completedSlugs = [], dismissedToday = false, personas = null } = {}) {
  if (dismissedToday) return null;
  const completedSet = new Set(completedSlugs);
  const remaining = LEARNING_CARDS.filter((c) => !completedSet.has(c.slug));
  if (remaining.length === 0) return null;
  if (!personas || personas.size === 0) return remaining[0];
  return sortByPersona(remaining, personas)[0] || null;
}

export function cardBySlug(slug) {
  return LEARNING_CARDS.find((c) => c.slug === slug) || null;
}
